/* prettier-ignore */

import type { ImageVariantWidth } from '../interfaces';

/**
 * HRS-3471: one `sizes` preset per rendering context.
 *
 * Values mirror the rendered image width (`getBoundingClientRect().width`)
 * measured with Playwright on production at the CSS breakpoints found in the
 * SCSS (viewport -> rendered width). The browser multiplies the result by the
 * device pixel ratio itself, so no manual DPR handling exists anywhere.
 * `includeOriginal` adds the original as a `1920w` candidate for slots that
 * render wider than 800 CSS px on desktop. `maxWidth` caps the candidates of
 * card-scale presets (combined with the connection-tier cap as the minimum).
 */
export type ImageSizesPreset =
  | 'carouselCard'
  | 'gridCard'
  | 'calendarCard'
  | 'plannerItem'
  | 'detailHero'
  | 'homeHero'
  | 'cityCard'
  | 'techPreview'
  | 'plannerThumbnail'
  | 'suggestedPointThumb'
  | 'smallCard'
  | 'registerItemSelector';

export interface ImageSizesPresetConfig {
  /** Value of the `sizes` attribute. */
  sizes: string;
  /** Whether the original is a srcset candidate (declared as `ORIGINAL_DECLARED_WIDTH`). */
  includeOriginal: boolean;
  /** Largest candidate width offered for this context (omitted = no preset cap). */
  maxWidth?: ImageVariantWidth;
}

/**
 * Card/thumbnail slots render at ~88-300 CSS px (techPreview up to ~490px), so on
 * high-DPR screens the browser would otherwise pick `w800`. Gabe's decision
 * (2026-09-25): cards never go above `w500`; heroes are not capped. Documented
 * exception: `registerItemSelector` is capped lower, at
 * `REGISTER_SELECTOR_MAX_VARIANT_WIDTH`.
 */
export const CARD_MAX_VARIANT_WIDTH: ImageVariantWidth = 500;

/**
 * HRS-3505 (supersedes the HRS-3510 `w200` cap): the CMS backfill
 * (`php artisan images:backfill-variants`, production 2026-10-02, ~144,660
 * variants) gave every legacy WebP register image (traveler types, categories;
 * 19/19 on fuengirola + emt) both `-w200.webp` and `-w350.webp`. Capping at
 * `w350` serves DPR 3 (288 device px) sharply while DPR 1-2 keep `w200`. Originals
 * narrower than 350px have no `w350` (the CMS never upscales) and fall back once
 * to the original through the directive's error handling.
 */
export const REGISTER_SELECTOR_MAX_VARIANT_WIDTH: ImageVariantWidth = 350;

export const IMAGE_SIZES_PRESETS: Readonly<Record<ImageSizesPreset, ImageSizesPresetConfig>> = {
  /** Home carousels (generic/route/event cards): 390->242, 749->242, 751->290, 1440->290, 1920->290 (SCSS max-width: 750px). */
  carouselCard: { sizes: '(max-width: 750px) 242px, 290px', includeOriginal: false, maxWidth: CARD_MAX_VARIANT_WIDTH },
  /** Category grid: 360->334 (1 col), 390->175, 500->236, 768->227, 1000->288, 1440->252, 1920->293. */
  gridCard: { sizes: '(max-width: 370px) calc(100vw - 26px), (max-width: 500px) 47vw, 300px', includeOriginal: false, maxWidth: CARD_MAX_VARIANT_WIDTH },
  /** Home schedule: 390->102, 768->195, 1000->271, 1001->126, 1440->260 (fluid column beside the calendar above 1000px). */
  calendarCard: { sizes: '(max-width: 1000px) 27vw, (max-width: 1580px) calc(30vw - 175px), 300px', includeOriginal: false, maxWidth: CARD_MAX_VARIANT_WIDTH },
  /** Legacy planner add-item grid: SCSS `minmax(175px, 1fr)`, 2 columns `minmax(155px, 1fr)` at <=475px. */
  plannerItem: { sizes: '(max-width: 475px) 50vw, 200px', includeOriginal: false, maxWidth: CARD_MAX_VARIANT_WIDTH },
  /** Detail hero: 390->374, 767->743, 768->632, 1440->1304 (padding 8px / 64px switch at 768px). */
  detailHero: { sizes: '(max-width: 767px) calc(100vw - 16px), calc(100vw - 136px)', includeOriginal: true },
  /** Home hero slider (full bleed): 390->390, 751->743, 1440->1432, 1920->1912. */
  homeHero: { sizes: '100vw', includeOriginal: true },
  /** Distributor cities: desktop rows 288px; mobile list (<=750px) 220px card scaled 1.1 -> 240px (measured at apply, montesdetoledo). */
  cityCard: { sizes: '(max-width: 750px) 240px, 288px', includeOriginal: false, maxWidth: CARD_MAX_VARIANT_WIDTH },
  /** Tech-page preview (measured at apply): 390->346, 600->556, 768->724, 850->784 (SCSS max-width: 850px), 900->485, 1920->485. */
  techPreview: { sizes: '(max-width: 850px) calc(100vw - 44px), 490px', includeOriginal: false, maxWidth: CARD_MAX_VARIANT_WIDTH },
  /** Legacy timeline/mobile cards, planner-2 card header and daily-point list: fixed 150px (day column max 148px). */
  plannerThumbnail: { sizes: '150px', includeOriginal: false, maxWidth: CARD_MAX_VARIANT_WIDTH },
  /** Planner-2 suggested point mobile header: SCSS fixed 88px. */
  suggestedPointThumb: { sizes: '88px', includeOriginal: false, maxWidth: CARD_MAX_VARIANT_WIDTH },
  /** Planner-2 suggested desktop card (160-200px square) and marker info card (`CARD_WIDTH = 200`). */
  smallCard: { sizes: '200px', includeOriginal: false, maxWidth: CARD_MAX_VARIANT_WIDTH },
  /** Register-user item selector (trip types, interests, subcategories dialog): fixed 96 x 96 at 390/DPR 2-3 and 1440/DPR 1; candidates w200 + w350 (HRS-3505). */
  registerItemSelector: { sizes: '96px', includeOriginal: false, maxWidth: REGISTER_SELECTOR_MAX_VARIANT_WIDTH },
};
